import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  TrendingUp,
  TrendingDown,
  Plus,
  X,
  RefreshCw,
  DollarSign,
  Target,
  Shield,
  ClipboardList,
  Trophy,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts";

const SOURCE_LABEL = {
  manual: { label: "Manual", color: "text-gray-300" },
  emergent_promise: { label: "💎 Promise", color: "text-purple-300" },
  sohel_top_pick: { label: "🏆 Top Pick", color: "text-amber-300" },
  sohel_pick: { label: "⭐ Sohel Pick", color: "text-yellow-300" },
};

function StatCard({ label, value, accent, icon: Icon, sub }) {
  return (
    <Card className="card-dark border-border">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground uppercase">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {label}
        </div>
        <p className={`text-xl md:text-2xl font-data font-bold mt-1 ${accent || "text-white"}`}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function NewTradeDialog({ open, onClose, onSubmitted }) {
  const [symbol, setSymbol] = useState("");
  const [side, setSide] = useState("LONG");
  const [qty, setQty] = useState("");
  const [entryPrice, setEntryPrice] = useState("");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setSymbol(""); setSide("LONG"); setQty(""); setEntryPrice(""); setStop(""); setTarget(""); setNotes("");
  };

  const submit = async () => {
    const sym = symbol.trim().toUpperCase();
    const q = parseFloat(qty);
    if (!sym) return toast.error("Symbol required");
    if (!q || q <= 0) return toast.error("Qty must be > 0");
    setBusy(true);
    try {
      const body = {
        symbol: sym,
        side,
        qty: q,
        source: "manual",
      };
      if (entryPrice) body.entry_price = parseFloat(entryPrice);
      if (stop) body.stop = parseFloat(stop);
      if (target) body.target = parseFloat(target);
      if (notes.trim()) body.notes = notes.trim();
      await api.post("/paper-portfolio/trades", body);
      toast.success(`✓ ${sym} ${side} opened`);
      reset();
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open trade");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-background border-cyan-500/30 max-w-md">
        <DialogHeader>
          <DialogTitle>New Paper Trade</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Symbol</label>
              <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="AAPL" className="bg-background/50" data-testid="pp-new-symbol" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Side</label>
              <Tabs value={side} onValueChange={setSide}>
                <TabsList className="w-full">
                  <TabsTrigger value="LONG" className="flex-1" data-testid="pp-new-side-long">LONG</TabsTrigger>
                  <TabsTrigger value="SHORT" className="flex-1" data-testid="pp-new-side-short">SHORT</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Qty</label>
              <Input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="10" type="number" className="bg-background/50" data-testid="pp-new-qty" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Entry Price (auto-live if empty)</label>
              <Input value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} placeholder="market" type="number" className="bg-background/50" data-testid="pp-new-entry" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Stop (optional)</label>
              <Input value={stop} onChange={(e) => setStop(e.target.value)} placeholder="—" type="number" className="bg-background/50" data-testid="pp-new-stop" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase">Target (optional)</label>
              <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="—" type="number" className="bg-background/50" data-testid="pp-new-target" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground uppercase">Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why I took this trade…" className="bg-background/50" data-testid="pp-new-notes" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full" data-testid="pp-new-submit">
            {busy ? "Opening…" : `Open ${side} Position`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PaperPortfolio() {
  const [trades, setTrades] = useState({ open_trades: [], closed_trades: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("open");
  const [showNew, setShowNew] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/paper-portfolio/trades"),
        api.get("/paper-portfolio/summary"),
      ]);
      setTrades(t.data || { open_trades: [], closed_trades: [] });
      setSummary(s.data || null);
    } catch (e) {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleClose = async (tradeId, symbol) => {
    if (!window.confirm(`Close ${symbol} at current market?`)) return;
    try {
      await api.post(`/paper-portfolio/trades/${tradeId}/close`, {});
      toast.success(`✓ ${symbol} closed`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close");
    }
  };

  const handleDelete = async (tradeId, symbol) => {
    if (!window.confirm(`Permanently delete ${symbol} record?`)) return;
    try {
      await api.delete(`/paper-portfolio/trades/${tradeId}`);
      toast.success(`Deleted ${symbol}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const fmtUSD = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
  const pnlAccent = (v) => v == null ? "text-white" : v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-gray-400";

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="paper-portfolio-page">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-cyan-400" />
            PAPER PORTFOLIO
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Practice trades with live prices · LONG &amp; SHORT · realized + unrealized P&amp;L · linked to Promises &amp; Top Picks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowNew(true)} data-testid="pp-new-btn">
            <Plus className="w-4 h-4 mr-1" />
            New Trade
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="pp-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
        <StatCard label="Total P&L" value={fmtUSD(summary?.total_pnl)} accent={pnlAccent(summary?.total_pnl)} icon={DollarSign} sub={`Realized ${fmtUSD(summary?.total_realized_pnl)} · Unrealized ${fmtUSD(summary?.total_unrealized_pnl)}`} />
        <StatCard label="Win Rate" value={`${summary?.win_rate ?? 0}%`} accent="text-cyan-400" icon={Trophy} sub={`${summary?.win_count ?? 0}W / ${summary?.loss_count ?? 0}L${summary?.current_streak ? ` · streak ${summary.current_streak.count} ${summary.current_streak.type}` : ""}`} />
        <StatCard label="Open" value={summary?.open_count ?? 0} accent="text-amber-400" />
        <StatCard label="Closed" value={summary?.closed_count ?? 0} accent="text-purple-400" />
        <StatCard label="Avg Return" value={fmtPct(summary?.avg_return_pct)} accent={pnlAccent(summary?.avg_return_pct)} sub={summary?.best_trade ? `Best: ${summary.best_trade.symbol} ${fmtPct(summary.best_trade.pnl_pct)}` : null} />
      </div>

      {/* Equity Curve + Per-Source ROI */}
      {(summary?.equity_curve?.length || 0) > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Equity Curve */}
          <Card className="card-dark border-border lg:col-span-2" data-testid="pp-equity-curve">
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-bold text-sm tracking-tight flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  EQUITY CURVE
                </h3>
                <span className="text-[10px] text-muted-foreground">Cumulative realized P&amp;L · {summary.equity_curve.length - 1} closed trade{(summary.equity_curve.length - 1) === 1 ? "" : "s"}</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart
                  data={summary.equity_curve.map((p) => ({
                    ...p,
                    label: p.t ? new Date(p.t).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "",
                    sym: p.trade?.symbol || "Start",
                  }))}
                  margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="ppEquityFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fill: "#9ca3af", fontSize: 10 }} stroke="#374151" />
                  <YAxis tick={{ fill: "#9ca3af", fontSize: 10 }} stroke="#374151" tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 4, fontSize: 11 }}
                    labelStyle={{ color: "#e5e7eb" }}
                    formatter={(value, _name, props) => {
                      const tr = props?.payload?.trade;
                      if (tr) {
                        return [`$${value} (after ${tr.symbol} ${tr.pnl >= 0 ? "+" : ""}${tr.pnl})`, "Cumulative P&L"];
                      }
                      return [`$${value}`, "Cumulative P&L"];
                    }}
                  />
                  <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="cumulative_pnl" stroke="#22c55e" strokeWidth={2} fill="url(#ppEquityFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Per-Source ROI */}
          <Card className="card-dark border-border" data-testid="pp-by-source">
            <CardContent className="p-3">
              <h3 className="font-heading font-bold text-sm tracking-tight mb-2 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                ROI BY SIGNAL SOURCE
              </h3>
              {Object.keys(summary.by_source || {}).length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No closed trades yet.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.by_source)
                    .sort((a, b) => (b[1].total_pnl || 0) - (a[1].total_pnl || 0))
                    .map(([src, b]) => {
                      const label = SOURCE_LABEL[src]?.label || src;
                      const labelColor = SOURCE_LABEL[src]?.color || "text-gray-300";
                      return (
                        <div key={src} className="p-2 rounded-sm bg-black/30 border border-border" data-testid={`pp-src-${src}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-bold ${labelColor}`}>{label}</span>
                            <span className={`text-xs font-data font-bold ${pnlAccent(b.total_pnl)}`}>
                              {fmtUSD(b.total_pnl)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                            <span>{b.closed_count} trades · {b.win_rate}% win</span>
                            <span className={pnlAccent(b.roi_pct)}>{fmtPct(b.roi_pct)} ROI</span>
                          </div>
                          {/* Mini progress bar */}
                          <div className="h-1 bg-black/40 rounded-full mt-1.5 overflow-hidden">
                            <div
                              className={`h-full ${b.total_pnl >= 0 ? "bg-emerald-500" : "bg-red-500"}`}
                              style={{ width: `${Math.min(100, Math.abs(b.win_rate))}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="open" data-testid="pp-tab-open">Open ({trades.open_trades.length})</TabsTrigger>
          <TabsTrigger value="closed" data-testid="pp-tab-closed">History ({trades.closed_trades.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Open trades */}
      {tab === "open" && (
        <Card className="card-dark border-border">
          <CardContent className="p-3">
            {trades.open_trades.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No open positions. Click <span className="text-white font-bold">New Trade</span> or take an Emergent Promise / Top Pick to open one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="pp-open-table">
                  <thead className="text-muted-foreground text-[10px] uppercase">
                    <tr className="border-b border-border">
                      <th className="text-left p-2">Symbol</th>
                      <th className="text-left p-2">Side</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Entry</th>
                      <th className="text-right p-2">Current</th>
                      <th className="text-right p-2">P&L</th>
                      <th className="text-right p-2">P&L %</th>
                      <th className="text-right p-2">Stop / Target</th>
                      <th className="text-left p-2">Source</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.open_trades.map((t) => {
                      const Trend = t.side === "LONG" ? TrendingUp : TrendingDown;
                      const sourceInfo = SOURCE_LABEL[t.source] || SOURCE_LABEL.manual;
                      return (
                        <tr key={t.trade_id} className="border-b border-border/30 hover:bg-white/5">
                          <td className="p-2 font-bold font-heading text-white">{t.symbol}</td>
                          <td className="p-2">
                            <Badge className={t.side === "LONG" ? "bg-emerald-500/15 text-emerald-300 border-0" : "bg-red-500/15 text-red-300 border-0"}>
                              <Trend className="w-3 h-3 mr-0.5" />
                              {t.side}
                            </Badge>
                          </td>
                          <td className="p-2 text-right font-data">{t.qty}</td>
                          <td className="p-2 text-right font-data">${t.entry_price}</td>
                          <td className="p-2 text-right font-data">{t.current_price ? `$${t.current_price}` : "—"}</td>
                          <td className={`p-2 text-right font-data font-bold ${pnlAccent(t.unrealized_pnl)}`}>{fmtUSD(t.unrealized_pnl)}</td>
                          <td className={`p-2 text-right font-data font-bold ${pnlAccent(t.unrealized_pnl_pct)}`}>{fmtPct(t.unrealized_pnl_pct)}</td>
                          <td className="p-2 text-right text-[11px]">
                            {t.stop ? <span className="text-red-300">${t.stop}</span> : <span className="text-muted-foreground">—</span>}
                            {" / "}
                            {t.target ? <span className="text-emerald-300">${t.target}</span> : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className={`p-2 text-[11px] ${sourceInfo.color}`}>{sourceInfo.label}</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="outline" onClick={() => handleClose(t.trade_id, t.symbol)} data-testid={`pp-close-${t.symbol}`}>
                              Close
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Closed trades */}
      {tab === "closed" && (
        <Card className="card-dark border-border">
          <CardContent className="p-3">
            {trades.closed_trades.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No closed trades yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="pp-closed-table">
                  <thead className="text-muted-foreground text-[10px] uppercase">
                    <tr className="border-b border-border">
                      <th className="text-left p-2">Symbol</th>
                      <th className="text-left p-2">Side</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Entry</th>
                      <th className="text-right p-2">Exit</th>
                      <th className="text-right p-2">P&L</th>
                      <th className="text-right p-2">P&L %</th>
                      <th className="text-left p-2">Source</th>
                      <th className="text-left p-2">Closed</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trades.closed_trades.map((t) => {
                      const sourceInfo = SOURCE_LABEL[t.source] || SOURCE_LABEL.manual;
                      const closedDate = t.exit_at ? new Date(t.exit_at).toLocaleDateString() : "—";
                      return (
                        <tr key={t.trade_id} className="border-b border-border/30 hover:bg-white/5">
                          <td className="p-2 font-bold text-white">{t.symbol}</td>
                          <td className="p-2 text-[11px] text-muted-foreground">{t.side}</td>
                          <td className="p-2 text-right font-data">{t.qty}</td>
                          <td className="p-2 text-right font-data">${t.entry_price}</td>
                          <td className="p-2 text-right font-data">${t.exit_price}</td>
                          <td className={`p-2 text-right font-data font-bold ${pnlAccent(t.pnl)}`}>{fmtUSD(t.pnl)}</td>
                          <td className={`p-2 text-right font-data font-bold ${pnlAccent(t.pnl_pct)}`}>{fmtPct(t.pnl_pct)}</td>
                          <td className={`p-2 text-[11px] ${sourceInfo.color}`}>{sourceInfo.label}</td>
                          <td className="p-2 text-[11px] text-muted-foreground">{closedDate}</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(t.trade_id, t.symbol)} data-testid={`pp-del-${t.symbol}`}>
                              <X className="w-3.5 h-3.5 text-red-400" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <NewTradeDialog open={showNew} onClose={() => setShowNew(false)} onSubmitted={refresh} />
    </div>
  );
}
